import { QueryClient, useInfiniteQuery, useMutation } from '@tanstack/react-query'

import { NOTIFICATION_SUBSCRIPTION_PAGE_SIZE, QUERY_KEY } from '@/lib/constants'
import {
  createWebPushNotificationSubscription,
  deleteWebPushNotificationSubscription,
  getAllWebPushNotificationSubscription,
} from '@/services/notification-subscription-service'
import { InfiniteQueryResponse } from '@/types/global'
import { WebPushNotificationSubscription } from '@/types/notification'

export function getPaginatedWebPushNotificationSubscriptionsQuery() {
  return {
    queryKey: [QUERY_KEY.WEB_PUSH_NOTIFICATION_SUBSCRIPTION],
    queryFn: async ({ pageParam = 1 }): Promise<InfiniteQueryResponse<WebPushNotificationSubscription>> => {
      const { ok, data, message } = await getAllWebPushNotificationSubscription({
        page: pageParam,
        limit: NOTIFICATION_SUBSCRIPTION_PAGE_SIZE,
      })

      if (!ok) {
        throw new Error(`Failed to fetch notification subscriptions: ${message}`)
      }

      return {
        items: data?.items ?? [],
        length: data?.length ?? 0,
        page: pageParam,
      }
    },
    initialPageParam: 1,
    placeholderData: {
      pages: [{ items: [], length: 0, page: 1 }],
      pageParams: [1],
    },
    getNextPageParam: (
      lastPage: InfiniteQueryResponse<WebPushNotificationSubscription>,
      allPages: InfiniteQueryResponse<WebPushNotificationSubscription>[],
    ) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0)

      return totalFetched >= lastPage.length ? undefined : lastPage.page + 1
    },
  }
}

export const useInfiniteWebPushNotificationSubscriptionsQuery = () => {
  return useInfiniteQuery(getPaginatedWebPushNotificationSubscriptionsQuery())
}

export const useWebPushNotificationSubscriptionCreateMutation = (queryClient: QueryClient) => {
  return useMutation({
    mutationFn: async (params: Parameters<typeof createWebPushNotificationSubscription>[0]) => {
      return await createWebPushNotificationSubscription(params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.WEB_PUSH_NOTIFICATION_SUBSCRIPTION] })
    },
  })
}

export const useWebPushNotificationSubscriptionDeleteMutation = (queryClient: QueryClient) => {
  return useMutation({
    mutationFn: async (params: Parameters<typeof deleteWebPushNotificationSubscription>[0]) => {
      return await deleteWebPushNotificationSubscription(params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.WEB_PUSH_NOTIFICATION_SUBSCRIPTION] })
    },
  })
}
